import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Check, Mic, Bot, Sparkles, PhoneCall, Zap, Loader2 } from "lucide-react";
import { Reveal, KineticLine } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES, STATS, TESTIMONIALS, PROCESS, PACKAGES } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1758691736483-5f600b509962?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const AI_WAVE = "https://images.unsplash.com/photo-1617994452722-4145e196248b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const AI_NET = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ACTION_IMGS = [
  { url: "https://images.unsplash.com/photo-1581092581294-a030b4f01a11?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", label: "Strategy sessions" },
  { url: "https://images.pexels.com/photos/15096572/pexels-photo-15096572.jpeg?auto=compress&cs=tinysrgb&w=1000", label: "Growth reviews" },
  { url: "https://images.pexels.com/photos/6476782/pexels-photo-6476782.jpeg?auto=compress&cs=tinysrgb&w=1000", label: "Data-driven results" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100vh] flex-col justify-center overflow-hidden pt-28 pb-16">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />
      <motion.div style={{ y }} className="pointer-events-none absolute -right-40 top-24 h-[520px] w-[520px] rounded-full bg-brand/20 blur-3xl" />
      <motion.div style={{ y: useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]) }} className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-brand/10 blur-3xl" />
      <motion.div style={{ opacity }} className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.15fr_1fr] lg:px-10">
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" /> Digital Marketing Agency
            </span>
          </Reveal>
          <h1 className="mt-8 font-display text-[3rem] font-black leading-[0.92] tracking-tighter text-slate-900 sm:text-6xl lg:text-[5.5rem]">
            <KineticLine delay={0.1}>Small business.</KineticLine>
            <KineticLine delay={0.24} className="text-brand">Massive growth.</KineticLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-slate-600">
              We help small businesses dominate online with high-converting websites, SEO, social media, paid ads, email & SMS, and reputation management — all engineered to grow real revenue.
            </p>
          </Reveal>
          <Reveal delay={0.6} className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-cta-button" className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">
              Get your free growth plan
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/services" data-testid="hero-services-link" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-4 text-sm font-semibold text-slate-900 transition-colors hover:border-brand hover:text-brand">
              Explore services
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.35}>
          <div className="relative">
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] border border-white/60 shadow-2xl shadow-brand/20">
              <img src={HERO_IMG} alt="Marketing team presenting a growth chart to clients" className="h-[440px] w-full object-cover lg:h-[520px]" />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/50 via-transparent to-brand/10" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
              className="absolute -bottom-6 -left-6 rounded-2xl border border-slate-100 bg-white/90 px-6 py-4 shadow-xl backdrop-blur"
            >
              <p className="font-display text-3xl font-black tracking-tight text-brand">+312%</p>
              <p className="text-xs font-medium text-slate-500">avg. client traffic growth</p>
            </motion.div>
          </div>
        </Reveal>
      </motion.div>
    </section>
  );
};

const AI_FEATURES = [
  { icon: Mic, title: "AI Voice Automation", body: "Human-sounding AI voice agents that answer, qualify and book leads 24/7 — so you never miss a call." },
  { icon: PhoneCall, title: "AI Lead Follow-up", body: "Instant, relentless follow-up across call, text and email the moment a lead comes in." },
  { icon: Bot, title: "24/7 AI Assistant", body: "An always-on assistant that chats with visitors, answers questions and captures details." },
  { icon: Sparkles, title: "AI-Assisted Content", body: "On-brand posts, pages and campaigns produced at machine speed, refined by our strategists." },
];

const AINative = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="relative z-10 overflow-hidden navy-mesh py-28 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-screen">
        <img src={AI_NET} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand backdrop-blur">
            <Zap className="h-3.5 w-3.5" /> AI-Native Agency
          </span>
          <h2 className="mt-6 font-display text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
            Marketing that never<br /><span className="text-brand">sleeps.</span>
          </h2>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-300">
            We build AI into everything we do. From voice agents that pick up every call to automated follow-up that closes the gap between "interested" and "customer" — your growth runs around the clock.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {AI_FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-colors hover:border-brand/50">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand text-white"><f.icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.2}>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950/50 p-7 shadow-2xl backdrop-blur-xl sm:p-9">
            <motion.div style={{ y: imgY }} className="pointer-events-none absolute inset-0 opacity-25">
              <img src={AI_WAVE} alt="" className="h-full w-full object-cover" />
            </motion.div>
            <div className="pointer-events-none absolute -inset-1 rounded-[2rem] bg-brand/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-white/85">
                  <span className="flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px] shadow-emerald-400 animate-pulse" />
                  Incoming call · answered by AI
                </span>
                <PhoneCall className="h-5 w-5 text-brand" />
              </div>

              <div className="mt-8 flex h-44 items-center justify-center gap-[3px]">
                {Array.from({ length: 44 }).map((_, i) => (
                  <motion.span
                    key={i}
                    className="w-1.5 rounded-full bg-gradient-to-t from-brand via-sky-400 to-sky-200"
                    style={{ transformOrigin: "center" }}
                    animate={{ height: [12, 78, 28, 104, 18, 62, 34, 12] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: (Math.abs(i - 22) * 0.07) }}
                  />
                ))}
              </div>

              <div className="mt-8">
                <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-6 py-5">
                  <motion.div
                    animate={{ boxShadow: ["0 0 0 0 rgba(0,82,255,0.5)", "0 0 0 14px rgba(0,82,255,0)"] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand"
                  >
                    <Mic className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <p className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">24 Hour AI Voice Agent</p>
                    <p className="text-sm text-slate-400">Always answering. Never sleeping.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Packages = () => {
  const [loadingKey, setLoadingKey] = useState(null);

  const startCheckout = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
  <section className="relative z-10 overflow-hidden py-28">
    <div className="pointer-events-none absolute inset-0 dot-bg opacity-70" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Packages</span>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Pick your growth plan.</h2>
        <p className="mt-4 text-lg text-slate-600">Two ways to grow with us — start lean and local, or go all-in with a fully AI-powered engine.</p>
      </div>
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {PACKAGES.map((p, idx) => (
          <Reveal key={p.name} delay={idx * 0.1}>
            <div
              data-testid={`package-${idx}`}
              className={`relative flex h-full flex-col rounded-3xl border p-8 lg:p-10 ${p.highlight ? "navy-mesh border-brand/40 text-white shadow-2xl shadow-brand/20" : "border-slate-200 bg-white"}`}
            >
              {p.highlight && (
                <span className="absolute right-8 top-8 rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">Most popular</span>
              )}
              <h3 className={`font-display text-2xl font-black tracking-tight ${p.highlight ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <span className={`font-display text-5xl font-black tracking-tighter ${p.highlight ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                <span className={`mb-1.5 text-sm font-semibold ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>.00{p.period}</span>
              </div>
              {p.inheritLabel && (
                <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-brand">
                  <Check className="h-3.5 w-3.5" /> {p.inheritLabel}
                </p>
              )}
              <ul className="mt-6 flex-1 space-y-3.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm font-medium ${p.highlight ? "text-slate-200" : "text-slate-700"}`}>
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${p.highlight ? "bg-brand text-white" : "bg-brand/10 text-brand"}`}><Check className="h-3 w-3" /></span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(p.lookupKey)}
                disabled={loadingKey !== null}
                data-testid={`package-cta-${idx}`}
                className={`mt-9 inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${p.highlight ? "bg-brand text-white hover:bg-brand-hover" : "border border-slate-300 text-slate-900 hover:border-brand hover:text-brand"}`}
              >
                {loadingKey === p.lookupKey ? (<><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>) : (<>{p.cta} <ArrowUpRight className="h-4 w-4" /></>)}
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-slate-500">Secure checkout powered by Stripe · cancel anytime — <Link to="/contact" className="font-semibold text-brand">questions? talk to us</Link>.</p>
    </div>
  </section>
  );
};

const Stats = () => (
  <section className="relative z-10 navy-mesh py-20 text-white">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-12 px-6 lg:grid-cols-4 lg:px-10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="text-center lg:text-left">
          <p className="font-display text-5xl font-black tracking-tighter text-brand sm:text-6xl">{s.value}</p>
          <p className="mt-2 text-sm text-slate-400">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Services = () => (
  <section className="relative z-10 py-28">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">What we do</span>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Six growth engines,<br />one relentless team.</h2>
        </div>
        <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-brand">All services <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 3) * 0.08}>
            <div data-testid={`service-card-${s.slug}`} className="group h-full bg-white p-8 transition-colors hover:bg-slate-50 lg:p-10">
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <s.icon className="h-6 w-6" />
                </div>
                <span className="font-display text-4xl font-black text-slate-100">{s.n}</span>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.short}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="relative z-10 navy-mesh py-28 text-white">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">How we grow you</span>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl">A proven system, tailored to your business.</h2>
      <div className="mt-16 space-y-px">
        {PROCESS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.05}>
            <div className="grid items-start gap-6 border-t border-white/10 py-10 md:grid-cols-[auto_1fr_2fr]">
              <span className="font-display text-6xl font-black leading-none text-brand">{p.n}</span>
              <h3 className="font-display text-2xl font-bold tracking-tight">{p.title}</h3>
              <p className="max-w-xl text-base leading-relaxed text-slate-400">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="relative z-10 py-28">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Loved by owners</span>
      <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Results that speak louder.</h2>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <figure data-testid={`testimonial-${i}`} className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-8">
              <blockquote className="text-lg font-medium leading-relaxed tracking-tight text-slate-900">“{t.quote}”</blockquote>
              <figcaption className="mt-8">
                <p className="font-bold text-slate-900">{t.name}</p>
                <p className="text-sm text-slate-500">{t.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PhotoBand = () => (
  <section className="relative z-10 overflow-hidden py-28">
    <div className="pointer-events-none absolute inset-0 dot-bg opacity-70" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">In the room</span>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Real people. Real growth.</h2>
        </div>
        <p className="max-w-sm text-slate-600">We sit down with you, dig into the data, and turn charts into a plan your business can feel.</p>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {ACTION_IMGS.map((img, i) => (
          <Reveal key={img.label} delay={i * 0.1}>
            <figure className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
              <img src={img.url} alt={img.label} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent" />
              <figcaption className="absolute bottom-5 left-5 font-display text-lg font-bold tracking-tight text-white">{img.label}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative z-10 pb-28">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-brand px-8 py-16 text-white lg:px-20 lg:py-24">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-4xl font-black tracking-tighter sm:text-6xl">Ready to grow?</h2>
            <p className="mt-5 text-lg text-white/80">Get a free, no-pressure growth audit. We'll show you exactly where you're leaving money on the table — and how to fix it.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              {["No contracts", "Free audit", "ROI focused"].map((f) => (
                <span key={f} className="inline-flex items-center gap-2 text-sm font-medium text-white/90"><Check className="h-4 w-4" /> {f}</span>
              ))}
            </div>
            <Link to="/contact" data-testid="cta-button" className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-brand transition-transform hover:scale-[1.02]">
              Claim your free audit <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <AINative />
      <Stats />
      <Services />
      <PhotoBand />
      <Process />
      <Packages />
      <Testimonials />
      <CTA />
    </div>
  );
}
